/**
 * Salary plan engine.
 *
 * Converts a SalaryPlan (career trajectory: list of levels with promote dates,
 * comp at each, and an optional career break) into a stream of dated USD
 * purchases that can be fed into the Future Plan projection chart.
 *
 * Three kinds of cash flow are emitted:
 *   - Biweekly:   every 14 days, USD = level.biweeklyOverrideUsd if set,
 *                 otherwise base x biweeklyToBaseRatio. Skipped during the
 *                 career break window.
 *   - Bonus:      annual on (payoutMonth, payoutDay), USD = bonus x bonusInvestRatio.
 *                 Paid for the year JUST ENDED, so the level active during
 *                 that year supplies the bonus amount, even if a promotion
 *                 happens on the same date. Skipped if the payout date falls
 *                 inside the career break.
 *   - NAP:        same date as bonus, USD = nap x napInvestRatio (when napUsd > 0).
 *                 Same skip behavior.
 *
 * The output is sorted by date ascending and ready to feed into the BTC stack
 * projection (same shape as the existing Future Plan scenarios).
 *
 * Patch U: per-level biweeklyOverrideUsd takes precedence over base x ratio.
 * When undefined, the value falls through to the computed product so
 * un-overridden levels still react to global ratio changes; overridden
 * levels stay pinned to whatever the user typed.
 *
 * Patch AG: split the seed ladder into two functions. defaultSalaryPlan()
 * is now a generic placeholder that any non-super-admin user starts with.
 * bainSalaryPlan() preserves Paul's original Bain consulting ladder for
 * the super-admin's seed-load path. The store still calls defaultSalaryPlan()
 * for fresh-state fallback and for resetSalaryPlan(), so editing the
 * generic default propagates to all call sites in one place.
 *
 * Patch AU: defaultSalaryPlan rebuilt as a VP/SVP/EVP ladder aimed at the
 * 45-55 executive demographic (target audience for the Bitcoin Retirement
 * Accelerator cohort). Minimum base 200k, multi-year tenures (3y VP, 4y
 * SVP, terminal EVP) so the projection chart shows a credible trajectory
 * without implying back-to-back annual promotions. NAP stays at zero for
 * all levels; most non-consulting executive comp isn't NAP-structured.
 * The UI is expected to hide the NAP column when all levels show zero
 * (AV follow-up). bainSalaryPlan() is untouched and continues to serve
 * the super-admin's reset-to-seed flow with the original Bain ladder
 * including its non-zero NAP values, so Paul's own UI keeps the NAP
 * column visible.
 *
 * Patch AW: custom buys. expandCustomBuysToPurchases() converts a list of
 * CustomBuy records (lump-sum or DCA windows) into the same
 * SalaryPlanPurchase shape that the salary expansion emits. The chart and
 * schedule on the Salary tab merge both streams. SalaryPlanPurchase.kind
 * gains 'custom-lump' and 'custom-dca' variants so the schedule can color
 * and badge them distinctly from biweekly / bonus / NAP entries. DCA math
 * is exact: totalUsd / numPeriods at every period tick from startDate to
 * endDate at the chosen frequency, so chart points are real dated buy
 * events, not an annualized smear.
 *
 * Patch AY: 'daily' added as a DCA frequency option. Step is one calendar
 * day; per-period amount works out to totalUsd / dayCount across the
 * window. The UI defaults new DCAs to daily because that's the cadence
 * the Bitcoin-stacking demographic intuitively reaches for (the classic
 * "stack sats every day" pattern). Daily DCA produces ~365 buy events
 * per year of window, which the chart handles fine but the bi-weekly
 * schedule table renders as 365 rows; the user can sanity-check against
 * the preview-line under each DCA row rather than scrolling the table.
 */

import { Decimal, ZERO, ONE } from './money.js';
import type { Usd } from './money.js';
import type { SalaryPlan, SalaryLevel, CustomBuy } from './types.js';

// ---------- Defaults from the user's comp table ----------

/** 30% of paycheck into BTC: 30 / (26 × 100) = 0.011538 */
export const DEFAULT_BIWEEKLY_RATIO = new Decimal(30).div(2600);
export const DEFAULT_BONUS_INVEST_RATIO = new Decimal('0.6');
export const DEFAULT_NAP_INVEST_RATIO = new Decimal('0.5');

/**
 * Default salary ladder for new users (Patch AU: rebuilt for the executive
 * demographic).
 *
 * stackbtc's cohort target audience is 45-55yo VP/SVP/director-level
 * executives. The ladder reflects that: three senior-IC/management levels
 * with 200k+ bases, multi-year tenures, and no NAP component (most non-
 * consulting executive comp isn't structured that way; the UI hides the
 * NAP column when all levels are zero). The user edits these toward their
 * actual comp; this is just an illustrative starting point that shows a
 * believable trajectory before any customization.
 *
 * Promote dates: VP at today (current role), SVP at +3 years, EVP at +7
 * years. Wider spacing than the prior 1-year cadence to match real
 * executive promotion intervals; the chart looks more like a real career
 * path and less like a consulting up-or-out conveyor.
 *
 * Annual bonus payout defaults to March 15 (most US firms pay Q1 for the
 * prior year's performance). The biweekly ratio and invest ratios inherit
 * the same module-level defaults bainSalaryPlan() uses, since those are
 * user-preference settings rather than industry-specific values.
 *
 * The Bain consulting ladder lives on as bainSalaryPlan() and is loaded
 * only via Paul's seed-load path (reset-to-seed footer button).
 */
export function defaultSalaryPlan(today: Date = new Date()): SalaryPlan {
  // VP/SVP/EVP ladder. Numbers in $thousands of base / bonus. NAP zero
  // throughout: most executive comp at this level is base + bonus + LTI
  // (which lives in equity, not modeled here). When the UI sees all
  // napUsd === 0 it hides the NAP column.
  const rows = [
    { name: 'VP',           base: 220, bonus: 80,  yearsAfterStart: 0 },
    { name: 'Senior VP',    base: 320, bonus: 160, yearsAfterStart: 3 },
    { name: 'Executive VP', base: 475, bonus: 275, yearsAfterStart: 7 },
  ];

  const levels: SalaryLevel[] = rows.map(row => {
    const promoteDate = new Date(today);
    promoteDate.setUTCFullYear(promoteDate.getUTCFullYear() + row.yearsAfterStart);
    return {
      id: `level-${row.name.toLowerCase().replace(/\s+/g, '-')}`,
      name: row.name,
      baseUsd: new Decimal(row.base * 1000),
      bonusUsd: new Decimal(row.bonus * 1000),
      napUsd: ZERO,
      promotedAt: promoteDate,
    };
  });

  return {
    levels,
    biweeklyToBaseRatio: DEFAULT_BIWEEKLY_RATIO,
    bonusInvestRatio: DEFAULT_BONUS_INVEST_RATIO,
    napInvestRatio: DEFAULT_NAP_INVEST_RATIO,
    payoutMonth: 12,
    payoutDay: 31,
  };
}

/**
 * Default income stage plan for bradlong subdomain.
 *
 * Three generic income stages (Current Income → Growth Stage → Peak Income)
 * with corp-career-sized numbers that work for 5-and-6-figure earners.
 * Labels are income-neutral: no corporate titles, no employment framing.
 * Users replace these with their actual sources (job, consulting, rental).
 *
 * Payout defaults to March 15 (same as defaultSalaryPlan). Bonus = $0
 * on the first two stages; the third adds a modest $50k bonus to represent
 * a performance incentive or surplus distribution. All NAP = 0.
 */
export function defaultIncomeStagePlan(today: Date = new Date()): SalaryPlan {
  const rows = [
    { name: 'Current income',  base: 150, bonus: 0,  yearsAfterStart: 0 },
    { name: 'Growth stage',    base: 220, bonus: 0,  yearsAfterStart: 2 },
    { name: 'Peak income',     base: 320, bonus: 50, yearsAfterStart: 5 },
  ];

  const levels: SalaryLevel[] = rows.map(row => {
    const promoteDate = new Date(today);
    promoteDate.setUTCFullYear(promoteDate.getUTCFullYear() + row.yearsAfterStart);
    return {
      id: `level-${row.name.toLowerCase().replace(/\s+/g, '-')}`,
      name: row.name,
      baseUsd: new Decimal(row.base * 1000),
      bonusUsd: new Decimal(row.bonus * 1000),
      napUsd: ZERO,
      promotedAt: promoteDate,
    };
  });

  return {
    levels,
    biweeklyToBaseRatio: DEFAULT_BIWEEKLY_RATIO,
    bonusInvestRatio: DEFAULT_BONUS_INVEST_RATIO,
    napInvestRatio: DEFAULT_NAP_INVEST_RATIO,
    payoutMonth: 3,
    payoutDay: 15,
  };
}

/**
 * Bain consulting ladder seeded from Paul's comp slide (Patch AG: renamed
 * from defaultSalaryPlan, now Paul-specific).
 *
 * Bases are post-September 1 "New Base" values from the slide. Bonuses are
 * "New Bonus" values. NAP defaults to the midpoint of the published range so
 * Paul starts with a sane, editable estimate; he can edit each level's NAP
 * up or down to reflect performance.
 *
 * Promote dates: starts with the user's currently-known promote (Sept 15
 * 2026 to Exp AC) and moves forward annually, except SM3 to AP1 which is 6
 * months. Today's date is May 2026 so "New AC" is the current row (no
 * promote date, it's the role they're already in; we set promotedAt to
 * "now" for ordering).
 *
 * Loaded into the store only for the super-admin via the seed-load flow.
 * Other users get defaultSalaryPlan() (VP/SVP/EVP, Patch AU) instead.
 */
export function bainSalaryPlan(today: Date = new Date()): SalaryPlan {
  const startOfPlan = today;
  const SEPT15 = (year: number): Date => new Date(`${year}-09-15T00:00:00Z`);
  const baseYear = today.getFullYear();
  // First annual promotion is the next September 15 from "today".
  // If today is past Sept 15 already, first promote is next year.
  const firstPromoteYear = today.getMonth() >= 8 && today.getDate() >= 15
    ? baseYear + 1
    : baseYear;

  // Promote schedule: annual Sept 15. SM3 only lasts 6 months.
  const levels: SalaryLevel[] = [
    { name: 'New AC',  base: 112, bonus: 15,  nap: 0,  duration: 1 },
    { name: 'Exp AC',  base: 117, bonus: 20,  nap: 0,  duration: 1 },
    { name: 'SAC',     base: 135, bonus: 32,  nap: 50, duration: 1 }, // SAC NAP range 37-62 mid ~ 50
    { name: 'New Cons',base: 192, bonus: 29,  nap: 0,  duration: 1 },
    { name: 'Exp Cons',base: 202, bonus: 34,  nap: 0,  duration: 1 },
    { name: 'Manager', base: 212, bonus: 52,  nap: 65, duration: 1 },
    { name: 'SM1',     base: 222, bonus: 92,  nap: 45, duration: 1 },
    { name: 'SM2',     base: 237, bonus: 102, nap: 50, duration: 1 },
    { name: 'SM3',     base: 247, bonus: 109, nap: 54, duration: 0.5 },
    { name: 'AP1',     base: 260, bonus: 124, nap: 90, duration: 1 },
    { name: 'AP2',     base: 270, bonus: 129, nap: 94, duration: 1 },
    { name: 'AP3',     base: 275, bonus: 134, nap: 98, duration: 1 },
  ].map((row, i, arr) => {
    // promotedAt for level i = sum of preceding durations, in months from
    // first promote date. We accumulate AS we map. To do that cleanly, do a
    // year accumulator.
    let promoteDate: Date;
    if (i === 0) {
      promoteDate = startOfPlan; // current role, they're already in it
    } else {
      // Sum durations of levels[1..i-1], add to firstPromoteYear's Sept 15.
      let yearsAfterFirst = 0;
      for (let j = 1; j < i; j++) yearsAfterFirst += arr[j - 1]!.duration;
      // Apply year offset; fractional years (0.5 from SM3) become months.
      const totalMonths = Math.round(yearsAfterFirst * 12);
      promoteDate = new Date(SEPT15(firstPromoteYear));
      promoteDate.setUTCMonth(promoteDate.getUTCMonth() + totalMonths);
    }

    const level: SalaryLevel = {
      id: `level-${row.name.toLowerCase().replace(/\s+/g, '-')}`,
      name: row.name,
      baseUsd: new Decimal(row.base * 1000),
      bonusUsd: new Decimal(row.bonus * 1000),
      napUsd: new Decimal(row.nap * 1000),
      promotedAt: promoteDate,
    };
    if (row.duration !== 1) level.durationYears = new Decimal(row.duration);
    return level;
  });

  return {
    levels,
    biweeklyToBaseRatio: DEFAULT_BIWEEKLY_RATIO,
    bonusInvestRatio: DEFAULT_BONUS_INVEST_RATIO,
    napInvestRatio: DEFAULT_NAP_INVEST_RATIO,
    payoutMonth: 12,
    payoutDay: 31,
  };
}

// ---------- Expansion ----------

export interface SalaryPlanPurchase {
  date: Date;
  usd: Usd;
  /** Patch AW: 'custom-lump' and 'custom-dca' added for custom-buy events.
   *  Salary-driven kinds ('biweekly' / 'bonus' / 'nap') retain their
   *  original meaning. The schedule UI uses this to badge and color
   *  rows distinctly. */
  kind: 'biweekly' | 'bonus' | 'nap' | 'custom-lump' | 'custom-dca';
  /** The level active when this cash flow was generated. Used for chart hover.
   *  For custom buys, this holds the buy's label (or a fallback like "Lump"
   *  or "DCA") so the schedule's Level column shows something meaningful. */
  levelName: string;
}

/** Sort levels in promotion order. Caller passes the plan; we don't mutate. */
function sortedLevels(plan: SalaryPlan): SalaryLevel[] {
  return [...plan.levels].sort((a, b) => a.promotedAt.getTime() - b.promotedAt.getTime());
}

/**
 * Returns the levels with EFFECTIVE promote dates: dates that account
 * for the career break. Any level whose original promote date falls on or
 * after the break start gets pushed forward by the break duration.
 *
 * Why derive instead of bake into the persisted promote dates: the user can
 * edit individual promote dates manually, and we don't want to silently
 * mutate those edits whenever the break moves. Keeping the stored dates as
 * "no-break baseline" means edits stay edits, and the break is purely a
 * presentation/calculation overlay.
 *
 * Example: if Exp AC promotes Sept 15, 2026 and the break runs Sept 2027
 * to Sept 2029, then SAC's stored "Sept 15 2027" promote becomes effective
 * "Sept 15 2029" (they come back to SAC after the 2-year break).
 */
export function effectiveLevels(plan: SalaryPlan): SalaryLevel[] {
  const sorted = sortedLevels(plan);
  if (!plan.career_break) return sorted;
  const breakStart = plan.career_break.breakStart.getTime();
  const breakDurationMs = plan.career_break.breakEnd.getTime() - breakStart;
  if (breakDurationMs <= 0) return sorted;
  return sorted.map(l => {
    if (l.promotedAt.getTime() < breakStart) return l;
    return { ...l, promotedAt: new Date(l.promotedAt.getTime() + breakDurationMs) };
  });
}

/**
 * Find the level active on a given date. The "active" level is the most
 * recent one whose effective promotedAt <= date. Returns null if date is
 * before the first level's effective promotedAt.
 */
function levelOn(plan: SalaryPlan, date: Date): SalaryLevel | null {
  const ls = effectiveLevels(plan);
  let active: SalaryLevel | null = null;
  for (const l of ls) {
    if (l.promotedAt.getTime() <= date.getTime()) active = l;
    else break;
  }
  return active;
}

/** Returns true if a date falls inside the career break window (inclusive on start, exclusive on end). */
function inBreak(plan: SalaryPlan, date: Date): boolean {
  if (!plan.career_break) return false;
  const t = date.getTime();
  return t >= plan.career_break.breakStart.getTime() && t < plan.career_break.breakEnd.getTime();
}

/**
 * Resolve the bi-weekly USD amount for a level. Patch U: if the level has
 * a `biweeklyOverrideUsd` set, that exact amount is used. Otherwise falls
 * through to `base x plan.biweeklyToBaseRatio`. Centralizing this here
 * keeps the override semantics in one place rather than scattered across
 * call sites.
 */
function biweeklyUsdFor(level: SalaryLevel, plan: SalaryPlan): Usd {
  return (level.biweeklyOverrideUsd ?? level.baseUsd.times(plan.biweeklyToBaseRatio)) as Usd;
}

/**
 * Expand a salary plan into a sorted list of dated purchase events from
 * `today` to `endDate`. Both biweekly and annual flows are generated.
 */
export function expandSalaryPlanToPurchases(
  plan: SalaryPlan,
  today: Date,
  endDate: Date,
): SalaryPlanPurchase[] {
  const out: SalaryPlanPurchase[] = [];
  const FOURTEEN_DAYS_MS = 14 * 24 * 3600_000;

  // ----- Biweekly stream -----
  // Cadence: every 14 days starting from `today`. We don't try to align with
  // the user's actual paycheck calendar (no signal for that); the projection
  // is close enough as long as the cadence is right.
  let biwDate = new Date(today);
  while (biwDate.getTime() <= endDate.getTime()) {
    if (!inBreak(plan, biwDate)) {
      const lvl = levelOn(plan, biwDate);
      if (lvl) {
        // Patch U: biweeklyUsdFor honors the per-level override. When the
        // level has no override, this is identical to the pre-U behavior
        // (baseUsd x plan.biweeklyToBaseRatio).
        const usd = biweeklyUsdFor(lvl, plan);
        if (usd.greaterThan(0)) {
          out.push({ date: new Date(biwDate), usd, kind: 'biweekly', levelName: lvl.name });
        }
      }
    }
    biwDate = new Date(biwDate.getTime() + FOURTEEN_DAYS_MS);
  }

  // ----- Annual bonus + NAP stream -----
  // Each year between today and endDate, on (payoutMonth, payoutDay), pay out
  // the bonus + NAP for the level that was active during the year just ended.
  // "The year just ended" means the 12 months PRIOR to the payout date,
  // i.e. the level active on (payout_date - 1 day) supplies the comp.
  for (let year = today.getUTCFullYear(); year <= endDate.getUTCFullYear() + 1; year++) {
    const payoutDate = new Date(Date.UTC(year, plan.payoutMonth - 1, plan.payoutDay));
    if (payoutDate.getTime() < today.getTime()) continue;
    if (payoutDate.getTime() > endDate.getTime()) break;
    if (inBreak(plan, payoutDate)) continue;

    // Level active the day before (avoids the same-day promote ambiguity).
    const dayBefore = new Date(payoutDate.getTime() - 24 * 3600_000);
    const lvl = levelOn(plan, dayBefore);
    if (!lvl) continue;

    if (lvl.bonusUsd.greaterThan(0) && plan.bonusInvestRatio.greaterThan(0)) {
      out.push({
        date: payoutDate,
        usd: lvl.bonusUsd.times(plan.bonusInvestRatio) as Usd,
        kind: 'bonus',
        levelName: lvl.name,
      });
    }
    // Patch BV: NAP buy amount now uses plan.bonusInvestRatio (the same %
    // the user sets for bonus) instead of a separate plan.napInvestRatio.
    // Paul's personal pref: NAP and bonus arrive on the same payout day
    // and should be treated as a single % control. napInvestRatio remains
    // in the SalaryPlan type as a vestigial field for back-compat with
    // persisted state; no UI reads or writes it after BV.
    if (lvl.napUsd.greaterThan(0) && plan.bonusInvestRatio.greaterThan(0)) {
      out.push({
        date: payoutDate,
        usd: lvl.napUsd.times(plan.bonusInvestRatio) as Usd,
        kind: 'nap',
        levelName: lvl.name,
      });
    }
  }

  out.sort((a, b) => a.date.getTime() - b.date.getTime());
  return out;
}

// ---------- Custom buys (Patch AW) ----------

/**
 * Expand a list of CustomBuy records into dated SalaryPlanPurchase events
 * in the window [today, endDate]. Two kinds handled:
 *
 *   - 'lump': emits a single event on startDate (when it falls inside the
 *     window). totalUsd is the single purchase amount.
 *   - 'dca': spreads totalUsd evenly across all period ticks from startDate
 *     to endDate (inclusive) at the chosen frequency. Per-period amount is
 *     totalUsd / numPeriods, computed against the FULL window even when the
 *     chart window clips some periods, so the user's "I'm spending $X total
 *     here" intention stays intact: a $48k / 12-month / biweekly DCA always
 *     emits ~$1846 per buy whether the chart shows 5 years or 30. Only
 *     periods inside [today, endDate] make it into the output.
 *
 * Frequency math:
 *   - weekly:   7-day step
 *   - biweekly: 14-day step
 *   - monthly:  1 calendar month step (preserves day-of-month, so monthly
 *               DCA stays aligned with payday rather than drifting forward
 *               two days every period the way a 30-day fixed step would).
 *
 * Invalid / incomplete buys are skipped silently:
 *   - lump with totalUsd <= 0
 *   - dca missing endDate or frequency
 *   - dca with endDate <= startDate
 *   - dca with totalUsd <= 0
 *
 * Output is sorted by date ascending and merges naturally with
 * expandSalaryPlanToPurchases at the call site (concatenate, sort, feed
 * the chart).
 */
export function expandCustomBuysToPurchases(
  buys: CustomBuy[],
  today: Date,
  endDate: Date,
): SalaryPlanPurchase[] {
  const out: SalaryPlanPurchase[] = [];

  for (const b of buys) {
    const label = b.label?.trim() || (b.kind === 'lump' ? 'Lump' : 'DCA');

    if (b.kind === 'lump') {
      if (!b.totalUsd.greaterThan(0)) continue;
      const t = b.startDate.getTime();
      if (t < today.getTime() || t > endDate.getTime()) continue;
      out.push({
        date: new Date(b.startDate),
        usd: b.totalUsd,
        kind: 'custom-lump',
        levelName: label,
      });
      continue;
    }

    // DCA branch.
    if (!b.endDate || !b.frequency) continue;
    if (b.endDate.getTime() <= b.startDate.getTime()) continue;
    if (!b.totalUsd.greaterThan(0)) continue;

    // Generate ALL period dates from startDate to endDate at the chosen
    // frequency, regardless of the chart window. We need the full count
    // to compute per-period USD correctly; per-period amount must be
    // totalUsd / numPeriods so the user's total spend intent is honored.
    const allDates: Date[] = [];
    if (b.frequency === 'daily' || b.frequency === 'weekly' || b.frequency === 'biweekly') {
      // Patch AY: daily added to the fixed-step branch. Day-count step
      // (1/7/14) is exact for all three since none of them care about
      // calendar boundaries.
      const stepDays = b.frequency === 'daily' ? 1
        : b.frequency === 'weekly' ? 7
        : 14;
      const stepMs = stepDays * 24 * 3600_000;
      let cur = b.startDate.getTime();
      while (cur <= b.endDate.getTime()) {
        allDates.push(new Date(cur));
        cur += stepMs;
      }
    } else {
      // Monthly: step by calendar month, preserving day-of-month. Using
      // setUTCMonth handles month-end edge cases (Jan 31 -> Feb 28/29)
      // by overflowing to the start of the next month, which is fine for
      // our purposes (a few days of drift over a year).
      let cur = new Date(b.startDate);
      while (cur.getTime() <= b.endDate.getTime()) {
        allDates.push(new Date(cur));
        const next = new Date(cur);
        next.setUTCMonth(next.getUTCMonth() + 1);
        cur = next;
      }
    }

    if (allDates.length === 0) continue;
    const perPeriodUsd = b.totalUsd.div(allDates.length) as Usd;

    for (const d of allDates) {
      const t = d.getTime();
      if (t < today.getTime() || t > endDate.getTime()) continue;
      out.push({
        date: new Date(d),
        usd: perPeriodUsd,
        kind: 'custom-dca',
        levelName: label,
      });
    }
  }

  out.sort((a, b) => a.date.getTime() - b.date.getTime());
  return out;
}

/**
 * Preview helper for the SalaryPlan UI: returns the number of periods and
 * per-period USD for a DCA buy, or null if the buy isn't a valid DCA.
 * Renders the "~$X / period across Y periods" hint under the row in the
 * Custom buys table without re-implementing the date-stepping logic.
 */
export function dcaPreview(b: CustomBuy): { numPeriods: number; perPeriodUsd: Usd } | null {
  if (b.kind !== 'dca' || !b.endDate || !b.frequency) return null;
  if (b.endDate.getTime() <= b.startDate.getTime()) return null;
  if (!b.totalUsd.greaterThan(0)) return null;

  let numPeriods = 0;
  if (b.frequency === 'daily' || b.frequency === 'weekly' || b.frequency === 'biweekly') {
    // Patch AY: daily added to the fixed-step branch. Same floor + 1
    // pattern as weekly/biweekly to include both endpoints.
    const stepDays = b.frequency === 'daily' ? 1
      : b.frequency === 'weekly' ? 7
      : 14;
    const stepMs = stepDays * 24 * 3600_000;
    numPeriods = Math.floor((b.endDate.getTime() - b.startDate.getTime()) / stepMs) + 1;
  } else {
    const months = (b.endDate.getUTCFullYear() - b.startDate.getUTCFullYear()) * 12
      + (b.endDate.getUTCMonth() - b.startDate.getUTCMonth());
    numPeriods = months + 1;
  }

  if (numPeriods <= 0) return null;
  return {
    numPeriods,
    perPeriodUsd: b.totalUsd.div(numPeriods) as Usd,
  };
}

// ---------- Time-to-next-promotion helper ----------

export interface CurrentLevelInfo {
  current: SalaryLevel | null;
  next: SalaryLevel | null;
  /** Fraction (0..1) of the way through the current level. */
  progress: Decimal;
  /** Days remaining until next promotion. */
  daysToNext: number;
}

export function currentLevelInfo(plan: SalaryPlan, today: Date): CurrentLevelInfo {
  const ls = effectiveLevels(plan);
  let current: SalaryLevel | null = null;
  let next: SalaryLevel | null = null;
  for (let i = 0; i < ls.length; i++) {
    const l = ls[i]!;
    if (l.promotedAt.getTime() <= today.getTime()) {
      current = l;
      next = ls[i + 1] ?? null;
    }
  }
  let progress = ZERO;
  let daysToNext = Infinity;
  if (current && next) {
    const span = next.promotedAt.getTime() - current.promotedAt.getTime();
    const elapsed = today.getTime() - current.promotedAt.getTime();
    progress = span > 0 ? Decimal.min(ONE, Decimal.max(0, new Decimal(elapsed).div(span))) : ZERO;
    daysToNext = Math.max(0, Math.ceil((next.promotedAt.getTime() - today.getTime()) / (24 * 3600_000)));
  }
  return { current, next, progress, daysToNext };
}
